import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getRecipesByShop,
  getRecipeInputValue,
  RESOURCES,
  NEW_RESOURCES,
  ALL_EQUIPMENT,
  type Recipe,
  type ShopCategory,
} from '@astra/shared';
import { networkManager } from '../../systems/NetworkManager';
import { emitGameEvent } from '../../core/GameEvents';
import {
  PALETTE, FONT_DATA, FONT_DISPLAY, StationHeader, StationFooter,
  stationContainerStyle, stationContentStyle, SectionTitle, primaryBtnStyle, getTierColor, inputStyle,
} from './stationTheme';

interface CraftingProps {
  stationId: string;
  onBack: () => void;
}

// Tabs map 1:1 to the shops seeded per station (1 Refinery + 4 Crafting).
const TABS: { category: ShopCategory; label: string; feePct: number }[] = [
  { category: 'ore', label: 'Refinery', feePct: 0.03 },
  { category: 'weapons', label: 'Weapons', feePct: 0.05 },
  { category: 'tools', label: 'Tools', feePct: 0.05 },
  { category: 'enhancers', label: 'Enhancers', feePct: 0.05 },
  { category: 'utilities', label: 'Utilities', feePct: 0.05 },
];

function resourceName(id: string): string {
  return (RESOURCES as Record<string, { name: string }>)[id]?.name
    ?? (NEW_RESOURCES as Record<string, { name: string }>)[id]?.name
    ?? id;
}
function outputName(recipe: Recipe): string {
  if (recipe.output.itemType === 'equipment') return ALL_EQUIPMENT[recipe.output.itemId]?.name ?? recipe.output.itemId;
  return resourceName(recipe.output.itemId);
}

export function Crafting({ stationId, onBack }: CraftingProps) {
  const [tab, setTab] = useState<ShopCategory>('ore');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [runs, setRuns] = useState(1);
  const [busy, setBusy] = useState(false);
  // Pooled materials the player holds across cargo + bank: itemId -> quantity.
  const [have, setHave] = useState<Record<string, number>>({});
  // Per-category shop ownership/fee, from the server.
  const [shops, setShops] = useState<Record<string, { ownerType: string; ownedByMe: boolean; feePct: number }>>({});
  // Per-category open auction (if any).
  const [auctions, setAuctions] = useState<Record<string, { basePrice: number; closesAt: string; highBid: number }>>({});
  const [bidAmount, setBidAmount] = useState<number>(0);

  const recipes = useMemo(() => getRecipesByShop(tab), [tab]);
  const selected = useMemo(() => recipes.find(r => r.id === selectedId) ?? null, [recipes, selectedId]);
  const shop = shops[tab];
  const feePct = shop?.feePct ?? TABS.find(t => t.category === tab)!.feePct;

  // Pull cargo + bank and shops.
  const refresh = useCallback(() => {
    networkManager.requestInventoryData(stationId);
    networkManager.requestPlayerBank();
    networkManager.requestShops(stationId);
    networkManager.requestAuctions(stationId);
  }, [stationId]);

  useEffect(() => {
    const cargo: Record<string, number> = {};
    const bank: Record<string, number> = {};
    const recompute = () => {
      const merged: Record<string, number> = { ...cargo };
      for (const [k, v] of Object.entries(bank)) merged[k] = (merged[k] ?? 0) + v;
      setHave(merged);
    };

    const prevInv = networkManager.onPlayerInventoryData;
    const prevBank = networkManager.onPlayerBank;
    const prevRefine = networkManager.onRefineResult;
    const prevCraft = networkManager.onCraftResult;
    const prevShops = networkManager.onShopsData;
    const prevShopAction = networkManager.onShopActionResult;
    const prevAuctions = networkManager.onAuctionsData;
    const prevBid = networkManager.onBidResult;

    networkManager.onPlayerInventoryData = (data) => {
      for (const k of Object.keys(cargo)) delete cargo[k];
      for (const it of data.items) if (it.itemType === 'resource') cargo[it.itemId] = (cargo[it.itemId] ?? 0) + it.quantity;
      recompute();
    };
    networkManager.onPlayerBank = (data) => {
      for (const k of Object.keys(bank)) delete bank[k];
      if (!data.error) for (const it of data.items) if (it.itemType === 'resource') bank[it.itemId] = (bank[it.itemId] ?? 0) + it.quantity;
      recompute();
    };
    const onResult = (data: { success: boolean; error?: string; outputQty?: number; feeCharged?: number }) => {
      setBusy(false);
      if (data.success) {
        emitGameEvent('notification', { type: 'success', message: `Crafted ×${data.outputQty ?? 1} (fee ${data.feeCharged ?? 0} cr)` });
        refresh();
      } else {
        emitGameEvent('notification', { type: 'error', message: data.error ?? 'Craft failed' });
      }
    };
    networkManager.onRefineResult = onResult;
    networkManager.onCraftResult = onResult;
    networkManager.onShopsData = (data) => {
      const map: Record<string, { ownerType: string; ownedByMe: boolean; feePct: number }> = {};
      for (const s of data.shops) map[s.category] = { ownerType: s.ownerType, ownedByMe: s.ownedByMe, feePct: s.feePct };
      setShops(map);
    };
    networkManager.onShopActionResult = (data) => {
      if (data.success) emitGameEvent('notification', { type: 'success', message: `Shop ${data.action ?? 'updated'}` });
      else emitGameEvent('notification', { type: 'error', message: data.error ?? 'Shop action failed' });
    };
    networkManager.onAuctionsData = (data) => {
      const map: Record<string, { basePrice: number; closesAt: string; highBid: number }> = {};
      for (const a of data.auctions) map[a.category] = { basePrice: a.basePrice, closesAt: a.closesAt, highBid: a.highBid };
      setAuctions(map);
    };
    networkManager.onBidResult = (data) => {
      if (data.success) emitGameEvent('notification', { type: 'success', message: `Bid placed (${data.highBid} cr)` });
      else emitGameEvent('notification', { type: 'error', message: data.error ?? 'Bid failed' });
      networkManager.requestAuctions(stationId);
    };

    refresh();
    return () => {
      networkManager.onPlayerInventoryData = prevInv;
      networkManager.onPlayerBank = prevBank;
      networkManager.onRefineResult = prevRefine;
      networkManager.onCraftResult = prevCraft;
      networkManager.onShopsData = prevShops;
      networkManager.onShopActionResult = prevShopAction;
      networkManager.onAuctionsData = prevAuctions;
      networkManager.onBidResult = prevBid;
    };
  }, [refresh, stationId]);

  const estFee = selected ? Math.floor(feePct * getRecipeInputValue(selected, runs)) : 0;
  const canCraft = useMemo(() => {
    if (!selected) return false;
    return selected.inputs.every(i => (have[i.itemId] ?? 0) >= i.quantity * runs);
  }, [selected, runs, have]);

  const doCraft = () => {
    if (!selected || busy) return;
    setBusy(true);
    if (selected.kind === 'refine') networkManager.sendRefine(stationId, selected.id, runs);
    else networkManager.sendCraft(stationId, selected.id, runs);
  };

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title="CRAFTING STATION" hint="Refine ore · craft equipment" />

      {/* category tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 16px 0' }}>
        {TABS.map(t => (
          <button
            key={t.category}
            onClick={() => { setTab(t.category); setSelectedId(null); }}
            style={{
              flex: 1, padding: '8px 4px', cursor: 'pointer',
              fontFamily: FONT_DISPLAY, fontSize: 13, letterSpacing: '0.05em', textTransform: 'uppercase',
              background: tab === t.category ? PALETTE.TEAL : 'transparent',
              color: tab === t.category ? PALETTE.DARK : PALETTE.LIGHT,
              border: `1px solid ${tab === t.category ? PALETTE.TEAL : PALETTE.BORDER}`,
              borderRadius: 4,
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* shop ownership bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, margin: '10px 16px 0', padding: '8px 12px',
        background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
        fontFamily: FONT_DATA, fontSize: 13, color: PALETTE.LIGHT,
      }}>
        <span style={{ color: PALETTE.DIM }}>Owner:</span>
        <span style={{ color: shop?.ownedByMe ? PALETTE.TEAL : PALETTE.LIGHT }}>
          {!shop || shop.ownerType === 'npc_station' ? 'Station (NPC)' : shop.ownedByMe ? 'You' : 'Another pilot'}
        </span>
        <span style={{ color: PALETTE.DIM, marginLeft: 'auto' }}>Fee:</span>
        <span style={{ color: PALETTE.YELLOW }}>{Math.round(feePct * 100)}%</span>
        {shop?.ownedByMe && (
          <>
            <button onClick={() => networkManager.sendSetShopFee(stationId, tab, Math.max(0, +(feePct - 0.01).toFixed(2)))} style={stepBtn}>−</button>
            <button onClick={() => networkManager.sendSetShopFee(stationId, tab, Math.min(0.25, +(feePct + 0.01).toFixed(2)))} style={stepBtn}>+</button>
            <button onClick={() => networkManager.sendReleaseShop(stationId, tab)} style={ownerBtn}>RELEASE</button>
          </>
        )}
      </div>

      {/* auction / bid bar — only for NPC shops (leasable). Acquisition = bidding. */}
      {shop && shop.ownerType === 'npc_station' && (() => {
        const a = auctions[tab];
        const minBid = a ? Math.max(a.basePrice, a.highBid + 1) : 1000;
        const closesIn = a ? Math.max(0, Math.round((new Date(a.closesAt).getTime() - Date.now()) / 60000)) : null;
        const amt = bidAmount > 0 ? bidAmount : minBid;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, margin: '8px 16px 0', padding: '8px 12px',
            background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
            fontFamily: FONT_DATA, fontSize: 13, color: PALETTE.LIGHT,
          }}>
            <span style={{ color: PALETTE.DIM }}>Auction:</span>
            {a ? (
              <span>high {a.highBid} cr · base {a.basePrice} · ends ~{closesIn}m</span>
            ) : (
              <span style={{ color: PALETTE.DIM }}>no bids yet — first bid opens it</span>
            )}
            <input
              type="number"
              value={amt}
              min={minBid}
              onChange={e => setBidAmount(parseInt(e.target.value) || 0)}
              style={{ ...inputStyle, width: 110, marginLeft: 'auto' }}
            />
            <button onClick={() => { networkManager.sendPlaceBid(stationId, tab, amt); setBidAmount(0); }} style={ownerBtn}>BID</button>
          </div>
        );
      })()}

      <div style={{ ...stationContentStyle, display: 'flex', gap: 12, padding: 16 }}>
        {/* left: recipe list */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', minWidth: 0 }}>
          <SectionTitle>Recipes</SectionTitle>
          {recipes.map(r => {
            const affordable = r.inputs.every(i => (have[i.itemId] ?? 0) >= i.quantity);
            return (
              <button
                key={r.id}
                onClick={() => { setSelectedId(r.id); setRuns(1); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                  fontFamily: FONT_DATA, fontSize: 13,
                  background: selectedId === r.id ? PALETTE.PANEL : 'transparent',
                  color: affordable ? PALETTE.LIGHT : PALETTE.DIM,
                  border: `1px solid ${selectedId === r.id ? PALETTE.TEAL : PALETTE.BORDER}`,
                  borderRadius: 4,
                }}
              >
                <span>{outputName(r)}</span>
                <span style={{ color: getTierColor(r.tier), fontSize: 11 }}>T{r.tier}</span>
              </button>
            );
          })}
        </div>

        {/* right: selected recipe detail */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <SectionTitle>Details</SectionTitle>
          {!selected ? (
            <div style={{ color: PALETTE.DIM, fontFamily: FONT_DATA, fontSize: 13, padding: 8 }}>Select a recipe.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT_DATA, fontSize: 13, color: PALETTE.LIGHT }}>
              <div style={{ fontSize: 15, color: PALETTE.LIGHT }}>
                {outputName(selected)} <span style={{ color: getTierColor(selected.tier) }}>T{selected.tier}</span>
                {' '}× {selected.output.quantity * runs}
              </div>

              <div>
                <div style={{ color: PALETTE.DIM, marginBottom: 4 }}>Inputs (have / need):</div>
                {selected.inputs.map(i => {
                  const need = i.quantity * runs;
                  const has = have[i.itemId] ?? 0;
                  const ok = has >= need;
                  return (
                    <div key={i.itemId} style={{ display: 'flex', justifyContent: 'space-between', color: ok ? PALETTE.LIGHT : PALETTE.RED }}>
                      <span>{resourceName(i.itemId)}</span>
                      <span>{has} / {need}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: PALETTE.DIM }}>Batch:</span>
                <button onClick={() => setRuns(r => Math.max(1, r - 1))} style={stepBtn}>−</button>
                <span style={{ minWidth: 24, textAlign: 'center' }}>{runs}</span>
                <button onClick={() => setRuns(r => Math.min(10, r + 1))} style={stepBtn}>+</button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', color: PALETTE.YELLOW }}>
                <span>Fee (est.)</span><span>{estFee} cr</span>
              </div>

              <button
                onClick={doCraft}
                disabled={!canCraft || busy}
                style={primaryBtnStyle(!canCraft || busy)}
              >
                {busy ? 'WORKING…' : selected.kind === 'refine' ? 'REFINE' : 'CRAFT'}
              </button>
              {!canCraft && <div style={{ color: PALETTE.RED, fontSize: 12 }}>Not enough materials.</div>}
            </div>
          )}
        </div>
      </div>

      <StationFooter>Materials are drawn from ship cargo + bank. Fee is charged in credits.</StationFooter>
    </div>
  );
}

const stepBtn: React.CSSProperties = {
  width: 28, height: 28, cursor: 'pointer', fontFamily: FONT_DATA, fontSize: 16,
  background: 'transparent', color: PALETTE.LIGHT, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
};

const ownerBtn: React.CSSProperties = {
  padding: '4px 10px', cursor: 'pointer', fontFamily: FONT_DISPLAY, fontSize: 12, letterSpacing: '0.05em',
  background: 'transparent', color: PALETTE.TEAL, border: `1px solid ${PALETTE.TEAL}`, borderRadius: 4,
};
